import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { FaWhatsapp, FaInstagram, FaLinkedin, FaEnvelope, FaPhone } from "react-icons/fa";
import logo from "@assets/logoMindbridge_1776100737645.png";
import { useLang } from "../hooks/useLang";
import { useTheme } from "../hooks/useTheme";

const GOLD = "#C9A84C";

export default function Footer() {
  const { t } = useTranslation();
  const { currentLang } = useLang();
  const { theme } = useTheme();
  const isRTL = currentLang === "ar";
  const isDark = theme === "dark";

  const socials = [
    { icon: FaWhatsapp,  href: "https://wa.me/212661710694",                        label: "WhatsApp"  },
    { icon: FaInstagram, href: "https://instagram.com/mindbridge_edtech",           label: "Instagram" },
    { icon: FaLinkedin,  href: "https://www.linkedin.com/company/mindbridge-edtech/", label: "LinkedIn"  },
  ];

  const links = [
    { href: "/", label: t("navbar.accueil") },
    { href: "/vision", label: t("navbar.vision") },
    { href: "/entreprise", label: t("navbar.entreprise") },
    { href: "/formations", label: t("navbar.formations") },
    { href: "/ecole", label: t("navbar.ecole") },
    { href: "/blog", label: t("navbar.blog") },
    { href: "/faq", label: t("navbar.faq") },
    { href: "/contact", label: t("navbar.contact") },
  ];

  return (
    <footer
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        background: isDark ? "#0d0f16" : "#1a1d26",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="MindBridge" className="h-12 w-12 object-contain" />
              <span className="text-white font-bold text-2xl">
                Mind<span style={{ color: GOLD }}>Bridge</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">{t("footer.tagline")}</p>
            <div className="flex gap-3 flex-wrap">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-white/15 text-gray-400 hover:text-white hover:border-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all duration-200"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{ color: GOLD }} className="font-semibold text-xs uppercase tracking-widest mb-4">Navigation</h3>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-gray-400 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 style={{ color: GOLD }} className="font-semibold text-xs uppercase tracking-widest mb-4">Contact</h3>
            <div className="space-y-3">
              <a href="tel:+212661710694" className="flex items-center gap-3 text-gray-400 hover:text-white text-sm transition-colors">
                <FaPhone size={13} style={{ color: GOLD }} />
                <span dir="ltr">+212.661.71.06.94</span>
              </a>
              <a href="mailto:contact@mind-bridge.education" className="flex items-center gap-3 text-gray-400 hover:text-white text-sm transition-colors">
                <FaEnvelope size={13} style={{ color: GOLD }} />
                <span>contact@mind-bridge.education</span>
              </a>
            </div>
          </div>
        </div>

        <div className={`mt-12 pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4 ${isRTL ? "sm:flex-row-reverse" : ""}`}>
          <p className="text-gray-300 text-sm">{t("footer.rights")}</p>
          <div dir="ltr" className="flex items-center gap-1 text-gray-300 text-sm flex-wrap justify-center">
            <span>{t("footer.crafted")}</span>
            <span style={{ color: GOLD }}>♥</span>
            <span>{t("footer.by_city")}</span>
            <a
              href="https://bidayatech.agency/?utm_source=sitex"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold transition-colors hover:underline"
              style={{ color: GOLD }}
            >
              BidayaTech
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
